import React, { useEffect } from 'react';
import cn from 'classnames';

import {
  BodyWrapper,
  dataTableStyles,
  HeaderRow,
  HeaderWrapper,
  TableEmptyRow,
} from '@creditflowresearch/uilib/Table/DataTable';
import Spinner from '@creditflowresearch/uilib/Loader/Spinner';
import Paginator from '@creditflowresearch/uilib/Paginator';

import { useTraceTableContext } from '@/creditflowresearch/application/Trace/TraceTableContext';
import { useFiltersContext } from '@/creditflowresearch/application/FiltersContext';
import { useUserTimeZoneContext } from '@/creditflowresearch/application/User/UserTimeZone/UserTimeZoneContext';

import { getColumns } from './getTradesColumns';

import { DEFAULT_EMPTY_MESSAGE } from '@framework/ui/Molecules/Tables/constants';
import TradesRow from './TradesRow';
import { TableFooterLabel } from '@/creditflowresearch/ui/components/trace/common/TableFooterLabel';
import { TRACE_MODULE } from '@/condor/ui/components/FinsightManager/constants';
import { useTrackAnalytics } from '@/creditflowresearch/application/useTrackAnalytics';

import spacesStyles from '@creditflowresearch/uilib/styles/helpers/spaces.module.scss';
import styles from '@/creditflowresearch/ui/components/bonds/common/BondTables/bondsTables.module.scss';

interface IProps {
  isItemsPerPageVisible: boolean,
  emptyMessage?: React.ReactNode,
}

const TraceTable = ({ isItemsPerPageVisible = true, emptyMessage = DEFAULT_EMPTY_MESSAGE }: IProps) => {
  const { traceData: { collection: data, totalCount }, isTraceFetching } = useTraceTableContext();
  const { timeZone } = useUserTimeZoneContext();
  const { trackAnalytics } = useTrackAnalytics();
  const {
    params: {
      sortBy,
      sortOrder,
      page,
      perPage,
    },
    handlePageChange,
    handleSortChange,
    handlePerPageChange,
  } = useFiltersContext();

  const paginate = (page: { selected: number }) => {
    handlePageChange(page.selected);
  };

  const itemsPerPage = (perPage: { value: number, label: string }) => {
    handlePerPageChange(perPage.value);
  };

  const sort = (sortBy: string, sortOrder: string) => {
    handleSortChange(sortBy, sortOrder);
  };

  const tableClass = cn(
    dataTableStyles.dataTable,
    dataTableStyles.isHoverable,
    dataTableStyles.nowrapCells,
  );

  const emptyMessageClass = cn(
    dataTableStyles.emptyMessage,
    styles.emptyMessage,
    spacesStyles.ptxl,
    spacesStyles.pbxl,
  );

  const onSort = (value, order) => {
    if (value === 'stringTradeSize') {
      sort('tradeSize', order);
    }
    sort(value, order);
  };

  const columns = getColumns(undefined, timeZone);

  useEffect(() => {
    trackAnalytics({ module: TRACE_MODULE });
  }, []);

  return (
    <>
      <div className={ cn(dataTableStyles.dataTableContainer, styles.tableContainer) }>
        { isTraceFetching && (
          <Spinner overlay />
        ) }
        <table className={ tableClass } data-test="traceTable">
          <HeaderWrapper>
            <HeaderRow
              sortBy={ sortBy }
              sortOrder={ sortOrder }
              onSortChange={ onSort }
              columns={ columns }
            />
          </HeaderWrapper>

          <BodyWrapper>
            { data?.length ? data.map((row, index) => (
              <TradesRow
                key={ row.id }
                rowIndex={ index }
                row={ row }
                timeZone={ timeZone }
              />
            )) : (
              <TableEmptyRow
                columnsCount={ columns.length }
                dataTest="traceTable"
                isFetching={ isTraceFetching }
                message={ emptyMessage }
                className={ emptyMessageClass }
              />
            ) }
          </BodyWrapper>
        </table>
      </div>
      <Paginator
        dataTest="traceTable"
        page={ page }
        perPage={ perPage }
        totalCount={ totalCount }
        onPageChange={ paginate }
        labelOfText={ <TableFooterLabel /> }
        onItemsPerPageChange={ itemsPerPage }
        isItemsPerPageVisible={ isItemsPerPageVisible }
      />
    </>
  );
};

export default TraceTable;
